import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  MoreVertical,
  Info,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import FormSkeleton from "../components/skeletons/FormSkeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { fieldService } from "../services/fieldService";
import datasetService from "../services/datasetService";
import { projectService } from "../services/projectService";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";

const FIELD_TYPES = [
  { value: "string", label: "String" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Text Area" },
  { value: "toggle", label: "Toggle (Boolean)" },
  { value: "url", label: "URL" },
  { value: "user", label: "User" },
  { value: "integer", label: "Integer" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select Dropdown" },
];

const EditFieldPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { fieldId } = useParams();
  const [searchParams] = useSearchParams();

  const storedClientId = getStoredClientId();
  const urlClientId = searchParams.get("client_id");
  const effectiveClientId = urlClientId || storedClientId || user?.client_id;

  const [fieldType, setFieldType] = useState("string");
  const [fieldName, setFieldName] = useState("");
  const [placeholderText, setPlaceholderText] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [applyToAllFuture, setApplyToAllFuture] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingField, setLoadingField] = useState(true);

  // Dataset state for dropdown/multiselect
  const [datasets, setDatasets] = useState([]);
  const [expandedDatasets, setExpandedDatasets] = useState({});
  const [selectedDataset, setSelectedDataset] = useState(null);

  useEffect(() => {
    if (effectiveClientId && fieldId) {
      loadField();
      loadProjects();
    }
  }, [effectiveClientId, fieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadField = async () => {
    try {
      setLoadingField(true);
      const response = await fieldService.getFieldById(fieldId);

      if (response.success && response.data?.field) {
        const field = response.data.field;
        setFieldType(field.type || "string");
        setFieldName(field.name || "");
        setPlaceholderText(field.placeholder_text || "");
        setIsMandatory(field.is_mandatory || false);

        // Set linked projects
        if (field.linked_projects && Array.isArray(field.linked_projects)) {
          setSelectedProjects(field.linked_projects.map((p) => p.project_id));
        }

        // Load datasets for dropdown/multiselect
        if (
          (field.type === "dropdown" || field.type === "multiselect") &&
          field.datasets
        ) {
          const datasetsWithValues = await Promise.all(
            field.datasets.map(async (dataset) => {
              try {
                const datasetResponse = await datasetService.getDatasetById(
                  dataset.dataset_id,
                );
                if (datasetResponse.success && datasetResponse.data?.dataset) {
                  return datasetResponse.data.dataset;
                }
                return dataset;
              } catch (e) {
                console.error("Error loading dataset values:", e);
                return dataset;
              }
            }),
          );
          setDatasets(
            datasetsWithValues.map((d, i) => ({
              ...d,
              id: d.dataset_id || `temp-${i}`,
            })),
          );
        }
      } else {
        toast.error("Field not found");
        navigate("/settings?tab=1");
      }
    } catch (error) {
      console.error("Error loading field:", error);
      toast.error("Failed to load field");
      navigate("/settings?tab=1");
    } finally {
      setLoadingField(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects({
        client_id: effectiveClientId,
        page: 1,
        limit: 1000,
        is_active: true,
        is_deleted: false,
      });
      if (response.success && response.data?.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      toast.error("Failed to load projects");
    }
  };

  const handleAddDataset = () => {
    const datasetNumber = datasets.length + 1;
    const newDataset = {
      id: `temp-${Date.now()}`,
      name: `Dataset #${datasetNumber}`,
      values: [],
      project_ids: [],
      apply_to_all_future_projects: false,
    };
    setDatasets([...datasets, newDataset]);
    setExpandedDatasets({ ...expandedDatasets, [newDataset.id]: true });
  };

  const handleDeleteDataset = async (datasetId) => {
    // If it's a real dataset (has dataset_id), delete it from backend
    const dataset = datasets.find((d) => d.id === datasetId);
    if (dataset?.dataset_id && !dataset.id.startsWith("temp-")) {
      try {
        await datasetService.deleteDataset(dataset.dataset_id);
        toast.success("Dataset deleted successfully");
      } catch (error) {
        console.error("Error deleting dataset:", error);
        toast.error("Failed to delete dataset");
        return;
      }
    }

    setDatasets(datasets.filter((d) => d.id !== datasetId));
    const newExpanded = { ...expandedDatasets };
    delete newExpanded[datasetId];
    setExpandedDatasets(newExpanded);
  };

  const handleAddValue = async (datasetId, valueName) => {
    if (!valueName.trim()) {
      toast.error("Value name is required");
      return;
    }

    const dataset = datasets.find((d) => d.id === datasetId);
    if (!dataset) return;

    // If dataset has a real ID, create value in backend
    if (dataset.dataset_id && !dataset.id.startsWith("temp-")) {
      try {
        const response = await datasetService.addDatasetValue(
          dataset.dataset_id,
          {
            value_name: valueName.trim(),
            is_default: false,
            position: dataset.values.length,
          },
        );
        if (response.success && response.data?.value) {
          const updatedDatasets = datasets.map((d) => {
            if (d.id === datasetId) {
              return {
                ...d,
                values: [...d.values, response.data.value],
              };
            }
            return d;
          });
          setDatasets(updatedDatasets);
          return;
        }
      } catch (error) {
        console.error("Error adding value:", error);
        toast.error("Failed to add value");
        return;
      }
    }

    // For temp datasets, add locally
    const updatedDatasets = datasets.map((d) => {
      if (d.id === datasetId) {
        const newValue = {
          id: `temp-value-${Date.now()}-${Math.random()}`,
          value_name: valueName.trim(),
          is_default: false,
          position: d.values.length,
        };
        return {
          ...d,
          values: [...d.values, newValue],
        };
      }
      return d;
    });
    setDatasets(updatedDatasets);
  };

  const handleToggleDefaultValue = async (datasetId, valueId) => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const value = dataset?.values.find(
      (v) => v.id === valueId || v.value_id === valueId,
    );
    if (!value) return;

    // If dataset has a real ID, update in backend
    if (dataset.dataset_id && !dataset.id.startsWith("temp-")) {
      try {
        const response = await datasetService.updateDatasetValue(
          dataset.dataset_id,
          value.value_id || value.id,
          { is_default: !value.is_default },
        );
        if (response.success) {
          // Reload dataset to get updated values
          const datasetResponse = await datasetService.getDatasetById(
            dataset.dataset_id,
          );
          if (datasetResponse.success && datasetResponse.data?.dataset) {
            const updatedDatasets = datasets.map((d) =>
              d.id === datasetId
                ? { ...d, values: datasetResponse.data.dataset.values }
                : d,
            );
            setDatasets(updatedDatasets);
          }
          return;
        }
      } catch (error) {
        console.error("Error updating default value:", error);
        toast.error("Failed to update default value");
        return;
      }
    }

    // For temp datasets, update locally
    const updatedDatasets = datasets.map((d) => {
      if (d.id === datasetId) {
        const updatedValues = d.values.map((v) => {
          if (v.id === valueId || v.value_id === valueId) {
            return { ...v, is_default: !v.is_default };
          } else if (v.is_default) {
            return { ...v, is_default: false };
          }
          return v;
        });
        return { ...d, values: updatedValues };
      }
      return d;
    });
    setDatasets(updatedDatasets);
  };

  const handleDeleteValue = async (datasetId, valueId) => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const value = dataset?.values.find(
      (v) => v.id === valueId || v.value_id === valueId,
    );
    if (!value) return;

    if (value.is_default) {
      toast.error("Cannot delete default value");
      return;
    }

    // If dataset has a real ID, delete from backend
    if (
      dataset.dataset_id &&
      !dataset.id.startsWith("temp-") &&
      value.value_id
    ) {
      try {
        await datasetService.deleteDatasetValue(
          dataset.dataset_id,
          value.value_id,
        );
        toast.success("Value deleted successfully");
      } catch (error) {
        console.error("Error deleting value:", error);
        toast.error("Failed to delete value");
        return;
      }
    }

    const updatedDatasets = datasets.map((d) => {
      if (d.id === datasetId) {
        return {
          ...d,
          values: d.values
            .filter((v) => v.id !== valueId && v.value_id !== valueId)
            .map((v, i) => ({ ...v, position: i })),
        };
      }
      return d;
    });
    setDatasets(updatedDatasets);
  };

  const handleEditValue = async (datasetId, valueId, newName) => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const value = dataset?.values.find(
      (v) => v.id === valueId || v.value_id === valueId,
    );
    if (!value) return;

    // If dataset has a real ID, update in backend
    if (
      dataset.dataset_id &&
      !dataset.id.startsWith("temp-") &&
      value.value_id
    ) {
      try {
        const response = await datasetService.updateDatasetValue(
          dataset.dataset_id,
          value.value_id,
          { value_name: newName.trim() },
        );
        if (response.success) {
          // Reload dataset to get updated values
          const datasetResponse = await datasetService.getDatasetById(
            dataset.dataset_id,
          );
          if (datasetResponse.success && datasetResponse.data?.dataset) {
            const updatedDatasets = datasets.map((d) =>
              d.id === datasetId
                ? { ...d, values: datasetResponse.data.dataset.values }
                : d,
            );
            setDatasets(updatedDatasets);
          }
          return;
        }
      } catch (error) {
        console.error("Error updating value:", error);
        toast.error("Failed to update value");
        return;
      }
    }

    // For temp datasets, update locally
    const updatedDatasets = datasets.map((d) => {
      if (d.id === datasetId) {
        return {
          ...d,
          values: d.values.map((v) =>
            v.id === valueId || v.value_id === valueId
              ? { ...v, value_name: newName.trim() }
              : v,
          ),
        };
      }
      return d;
    });
    setDatasets(updatedDatasets);
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const handleSelectAllProjects = () => {
    const filteredProjects = getFilteredProjects();
    if (selectedProjects.length === filteredProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(filteredProjects.map((p) => p.project_id));
    }
  };

  const getFilteredProjects = () => {
    if (!projectSearchQuery.trim()) {
      return projects;
    }
    const query = projectSearchQuery.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.project_id?.toLowerCase().includes(query),
    );
  };

  const handleUpdateField = async () => {
    if (!fieldName.trim()) {
      toast.error("Field name is required");
      return;
    }

    try {
      setLoading(true);

      // Update field
      const fieldData = {
        name: fieldName.trim(),
        type: fieldType,
        is_mandatory: isMandatory,
        placeholder_text: placeholderText.trim() || null,
        project_ids: selectedProjects,
        client_id: effectiveClientId,
      };

      const fieldResponse = await fieldService.updateField(fieldId, fieldData);

      if (!fieldResponse.success) {
        throw new Error(fieldResponse.message || "Failed to update field");
      }

      // If dropdown/multiselect, update/create datasets
      if (fieldType === "dropdown" || fieldType === "multiselect") {
        for (const dataset of datasets) {
          try {
            if (dataset.dataset_id && !dataset.id.startsWith("temp-")) {
              // Update existing dataset
              await datasetService.updateDataset(dataset.dataset_id, {
                name: dataset.name,
                project_ids: dataset.project_ids || [],
              });
            } else {
              // Create new dataset
              const datasetData = {
                field_id: fieldId,
                name: dataset.name,
                values: dataset.values.map((v) => ({
                  value_name: v.value_name,
                  is_default: v.is_default,
                  position: v.position,
                })),
                project_ids: dataset.project_ids || [],
                client_id: effectiveClientId,
              };
              await datasetService.createDataset(datasetData);
            }
          } catch (error) {
            console.error("Error saving dataset:", error);
            toast.error(`Failed to save dataset "${dataset.name}"`);
          }
        }
      }

      toast.success("Field updated successfully");
      navigate("/settings?tab=1");
    } catch (error) {
      console.error("Error updating field:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update field",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDatasetProjectLink = (dataset) => {
    setSelectedDataset(dataset);
    setSelectedProjects(dataset.project_ids || []);
    setApplyToAllFuture(dataset.apply_to_all_future_projects || false);
    setShowProjectModal(true);
  };

  const handleUpdateDatasetProjects = () => {
    if (selectedDataset) {
      const updatedDatasets = datasets.map((d) =>
        d.id === selectedDataset.id
          ? {
              ...d,
              project_ids: selectedProjects,
              apply_to_all_future_projects: applyToAllFuture,
            }
          : d,
      );
      setDatasets(updatedDatasets);
    } else {
      // Regular field project linking
      // This is handled in the fieldData
    }
    setShowProjectModal(false);
    setSelectedDataset(null);
    setProjectSearchQuery("");
  };

  if (loadingField) {
    return (
      <div className="min-h-[400px] p-4">
        <FormSkeleton fields={8} />
      </div>
    );
  }

  const filteredProjects = getFilteredProjects();

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/settings?tab=1")}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Edit Field</h1>
          <p className="text-gray-600 mt-1">
            Update custom field configuration
          </p>
        </div>
      </div>

      {/* Field Type */}
      <div className="card p-6">
        <div>
          <Label htmlFor="field-type">Type</Label>
          <Select value={fieldType} onValueChange={setFieldType} disabled>
            <SelectTrigger id="field-type" className="mt-1">
              <SelectValue placeholder="Select field type" />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 mt-2">
            Field type cannot be changed after creation.
          </p>
        </div>
      </div>

      {/* Field Details */}
      <div className="card p-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Field Name *
            </label>
            <Input
              type="text"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="Enter field name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Placeholder text
            </label>
            <Input
              type="text"
              value={placeholderText}
              onChange={(e) => setPlaceholderText(e.target.value)}
              placeholder="Enter placeholder name"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="is-mandatory"
            checked={isMandatory}
            onCheckedChange={setIsMandatory}
          />
          <Label htmlFor="is-mandatory" className="cursor-pointer">
            Mark this field as required
          </Label>
        </div>
      </div>

      {/* Dataset Section for Dropdown/Multiselect */}
      {(fieldType === "dropdown" || fieldType === "multiselect") && (
        <div className="card p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-text-primary">Datasets</h3>
            <button
              onClick={handleAddDataset}
              disabled={datasets.length >= 30}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Dataset</span>
            </button>
          </div>

          {datasets.map((dataset, datasetIndex) => (
            <div
              key={dataset.id}
              className="border border-gray-200 rounded-lg p-4 space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-text-primary">
                    {dataset.name}
                  </h4>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Dataset information"
                  >
                    <Info className="w-4 h-4 text-gray-400" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteDataset(dataset.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-sm text-gray-600">
                Values within this dropdown: {dataset.values?.length || 0}
              </p>

              {dataset.values && dataset.values.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          TITLE
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          DEFAULT
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          ACTIONS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataset.values.map((value) => (
                        <tr
                          key={value.id || value.value_id}
                          className="border-t border-gray-200"
                        >
                          <td className="px-4 py-2 text-sm">
                            {value.value_name}
                          </td>
                          <td className="px-4 py-2">
                            <Switch
                              checked={value.is_default || false}
                              onCheckedChange={() =>
                                handleToggleDefaultValue(
                                  dataset.id,
                                  value.id || value.value_id,
                                )
                              }
                            />
                          </td>
                          <td className="px-4 py-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDataset(dataset);
                                  }}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem
                                  onClick={() => {
                                    const newName = prompt(
                                      "Enter new value name:",
                                      value.value_name,
                                    );
                                    if (newName && newName.trim()) {
                                      handleEditValue(
                                        dataset.id,
                                        value.id || value.value_id,
                                        newName.trim(),
                                      );
                                    }
                                    setSelectedDataset(null);
                                  }}
                                >
                                  Edit Value
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    handleDeleteValue(
                                      dataset.id,
                                      value.id || value.value_id,
                                    );
                                    setSelectedDataset(null);
                                  }}
                                  disabled={value.is_default}
                                  className="text-red-600"
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(!dataset.values || dataset.values.length === 0) && (
                <p className="text-sm text-gray-500 italic">
                  No values added yet. Enter name and click on Add Value to
                  proceed.
                </p>
              )}

              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Enter value name"
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddValue(dataset.id, e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
                <Button
                  onClick={(e) => {
                    const input = e.target.previousElementSibling;
                    handleAddValue(dataset.id, input.value);
                    input.value = "";
                  }}
                >
                  Add Value
                </Button>
              </div>

              {/* Link Dataset to Project */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-700">
                      Link Dataset to Project
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedProjects(dataset.project_ids || []);
                      handleDatasetProjectLink(dataset);
                    }}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Update
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {dataset.project_ids?.length || 0} Projects linked
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link Field to Projects (for non-dropdown fields) */}
      {fieldType !== "dropdown" && fieldType !== "multiselect" && (
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-1">
                Link Field to Projects
              </h3>
              <p className="text-sm text-gray-600">
                {selectedProjects.length}{" "}
                {selectedProjects.length === 1 ? "Project" : "Projects"}{" "}
                selected
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedDataset(null);
                setShowProjectModal(true);
              }}
              className="btn-primary px-4 py-2"
            >
              Update
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={() => navigate("/settings?tab=1")}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdateField}
          disabled={loading || !fieldName.trim()}
        >
          {loading ? "Updating..." : "Update"}
        </Button>
      </div>

      {/* Value Menu - Now handled inline in DropdownMenu above */}

      {/* Project Selection Modal */}
      <Dialog
        open={showProjectModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowProjectModal(false);
            setSelectedDataset(null);
            setProjectSearchQuery("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDataset
                ? `Link Dataset to Projects`
                : `Link Field to Projects`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="apply-to-all-future"
                checked={applyToAllFuture}
                onCheckedChange={setApplyToAllFuture}
              />
              <Label htmlFor="apply-to-all-future" className="cursor-pointer">
                Apply to All Future Projects
              </Label>
            </div>
            {applyToAllFuture && (
              <p className="text-sm text-text-tertiary ml-10">
                This option allows you to use this{" "}
                {selectedDataset ? "dataset" : "field"} for all future projects.
              </p>
            )}

            <div className="relative">
              <Input
                type="text"
                placeholder="Search projects by title/ID"
                value={projectSearchQuery}
                onChange={(e) => setProjectSearchQuery(e.target.value)}
                className="pl-10"
              />
              <Search className="absolute left-3 top-3 w-4 h-4 text-text-tertiary" />
            </div>

            <div className="border border-border-primary rounded-lg max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-bg-secondary sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <Checkbox
                        checked={
                          filteredProjects.length > 0 &&
                          filteredProjects.every((p) =>
                            selectedProjects.includes(p.project_id),
                          )
                        }
                        onCheckedChange={handleSelectAllProjects}
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                      ID
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                      TITLE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr key={project.project_id} className="hover:bg-bg-hover">
                      <td className="px-4 py-2">
                        <Checkbox
                          checked={selectedProjects.includes(
                            project.project_id,
                          )}
                          onCheckedChange={() =>
                            handleProjectToggle(project.project_id)
                          }
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-text-primary">
                        {project.project_id?.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-2 text-sm text-text-primary">
                        {project.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowProjectModal(false);
                setSelectedDataset(null);
                setProjectSearchQuery("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateDatasetProjects}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditFieldPage;
